import { ICollectionMetadata } from "@novel/shared/interfaces/CollectionMetadata";
import { INovelUserShallow } from "@novel/shared/interfaces/NovelUser";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import { customerGalleryPrefix, IPFS_GATEWAY_PREFIX } from "@novel/shared/utils/appConstants";
import { appPrefixedHostWithProtocol } from "@novel/shared/utils/appDomainConstants";

export function getNovelCustomerPublicUrl(novelCustomer: INovelUserShallow): string {
    return `${appPrefixedHostWithProtocol}${customerGalleryPrefix}/${novelCustomer.id}`;
}

enum SupportedBlockchainTypeEnum {
    evm = "evm",
    solana = "solana",
}

type SwaggerGeneratedBlockchainTypeEnum = "evm" | "solana";

export type SupportedBlockchainType =
    | SupportedBlockchainTypeEnum
    | SwaggerGeneratedBlockchainTypeEnum;

enum SupportedConnectionAppsEnum {
    torus = "torus",
    metamask = "metamask",
    coinbase = "coinbase",
    phantom = "phantom",
    walletConnect = "wallet-connect",
    walletConnectV2 = "wallet-connect-v2",
}

type SwaggerGeneratedSupportedConnectionAppsEnum =
    | "torus"
    | "wallet-connect"
    | "metamask"
    | "coinbase"
    | "wallet-connect-v2"
    | "phantom";

export type SupportedConnectionApps =
    | SupportedConnectionAppsEnum
    | SwaggerGeneratedSupportedConnectionAppsEnum;

export enum SupportedMintingBlockchainsEnum {
    polygon = "polygon",
    mumbai = "mumbai",
}

export type SwaggerGeneratedSupportedMintingBlockchainsEnum = "polygon" | "mumbai";

export type SupportedMintingBlockchain =
    | SupportedMintingBlockchainsEnum
    | SwaggerGeneratedSupportedMintingBlockchainsEnum;

enum SupportedImportBlockchains {
    polygon = "polygon",
    mumbai = "mumbai",
    ethereum = "ethereum",
    ropsten = "ropsten",
    rinkeby = "rinkeby",
    goerli = "goerli",
    solana = "solana",
}

export type SwaggerGeneratedImportBlockchains =
    | "ethereum"
    | "polygon"
    | "ropsten"
    | "rinkeby"
    | "goerli"
    | "mumbai"
    | "solana";

export type SupportedBlockchain =
    | SupportedMintingBlockchain
    | SupportedImportBlockchains
    | SwaggerGeneratedImportBlockchains;

export type SmartContractType = "721" | "1155" | "20" | "custom";

export function getIsTestnet(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
): boolean {
    switch (blockchain) {
        case SupportedImportBlockchains.ropsten:
        case SupportedImportBlockchains.rinkeby:
        case SupportedImportBlockchains.goerli:
        case SupportedImportBlockchains.mumbai:
            return true;
        default:
            return false;
    }
}

export function getIsPolygon(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
): boolean {
    switch (blockchain) {
        case SupportedImportBlockchains.polygon:
        case SupportedImportBlockchains.mumbai: {
            return true;
        }
        default: {
            return false;
        }
    }
}

export function getBlockchainTypeFromBlockchain(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
): SupportedBlockchainType {
    switch (blockchain) {
        case SupportedImportBlockchains.solana: {
            return "solana";
        }
        default: {
            return "evm";
        }
    }
}

export function getSecondaryMarketTokenLink(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
    address: string,
    tokenId: string,
): string | undefined {
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://opensea.io/assets/${address}/${tokenId}`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://testnets.opensea.io/assets/${address}/${tokenId}`;
        }
        // case SupportedImportBlockchains.ropsten: {
        //     return `https://testnets.opensea.io/assets/${address}/${tokenId}`;
        // }
        case SupportedImportBlockchains.goerli: {
            return `https://testnets.opensea.io/assets/${address}/${tokenId}`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://opensea.io/assets/matic/${address}/${tokenId}`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://testnets.opensea.io/assets/mumbai/${address}/${tokenId}`;
        }
        case SupportedImportBlockchains.solana: {
            return `https://opensea.io/assets/solana/${tokenId}`;
        }
    }
}

export function getSecondaryMarketCollectionLink(
    collectionMetadata: ICollectionMetadata,
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
): string | undefined {
    if (collectionMetadata.openSeaSlug) {
        if (getIsTestnet(blockchain)) {
            return `https://testnets.opensea.io/collection/${collectionMetadata.openSeaSlug}`;
        }

        return `https://opensea.io/collection/${collectionMetadata.openSeaSlug}`;
    }
}

export function secondaryMarketCryptoAddressLink(
    blockchainType: SupportedBlockchainType,
    address: string,
    forAvalanche = false,
): string | undefined {
    if (forAvalanche) {
        return `https://nftrade.com/users/avalanche/${address}`;
    }

    if (blockchainType === "evm" || blockchainType === "solana") {
        return `https://opensea.io/${address}`;
    }
}

export function getBlockchainCollectionLink(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
    address: string,
    hasAtLeastOneTokenMinted = false,
): string | undefined {
    const pathString = hasAtLeastOneTokenMinted ? "token" : "address";
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://etherscan.io/${pathString}/${address}`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://rinkeby.etherscan.io/${pathString}/${address}`;
        }
        case SupportedImportBlockchains.ropsten: {
            return `https://ropsten.etherscan.io/${pathString}/${address}`;
        }
        case SupportedImportBlockchains.goerli: {
            return `https://goerli.etherscan.io/${pathString}/${address}`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://polygonscan.com/${pathString}/${address}`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://mumbai.polygonscan.com/${pathString}/${address}`;
        }
    }
}

export function getBlockchainTokenLink(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
    address: string,
    tokenId: string,
): string | undefined {
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://etherscan.io/token/${address}?a=${tokenId}`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://rinkeby.etherscan.io/token/${address}?a=${tokenId}`;
        }
        case SupportedImportBlockchains.ropsten: {
            return `https://ropsten.etherscan.io/token/${address}?a=${tokenId}`;
        }
        case SupportedImportBlockchains.goerli: {
            return `https://goerli.etherscan.io/token/${address}?a=${tokenId}`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://polygonscan.com/token/${address}?a=${tokenId}`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://mumbai.polygonscan.com/token/${address}?a=${tokenId}`;
        }
        case SupportedImportBlockchains.solana: {
            return `https://explorer.solana.com/address/${tokenId}`;
        }
    }
}

export function getBlockchainTransactionLink(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
    transactionHash: string,
): string | undefined {
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://etherscan.io/tx/${transactionHash}`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://rinkeby.etherscan.io/tx/${transactionHash}`;
        }
        case SupportedImportBlockchains.ropsten: {
            return `https://ropsten.etherscan.io/tx/${transactionHash}`;
        }
        case SupportedImportBlockchains.goerli: {
            return `https://goerli.etherscan.io/tx/${transactionHash}`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://polygonscan.com/tx/${transactionHash}`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://mumbai.polygonscan.com/tx/${transactionHash}`;
        }
        case SupportedImportBlockchains.solana: {
            return `https://explorer.solana.com/tx/${transactionHash}`;
        }
    }
}

export function getBlockchainContractLink(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
    contractAddress: string,
): string | undefined {
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://etherscan.io/address/${contractAddress}`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://rinkeby.etherscan.io/address/${contractAddress}`;
        }
        case SupportedImportBlockchains.ropsten: {
            return `https://ropsten.etherscan.io/address/${contractAddress}`;
        }
        case SupportedImportBlockchains.goerli: {
            return `https://goerli.etherscan.io/address/${contractAddress}`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://polygonscan.com/address/${contractAddress}`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://mumbai.polygonscan.com/address/${contractAddress}`;
        }
        case SupportedImportBlockchains.solana: {
            return `https://explorer.solana.com/address/${contractAddress}`;
        }
    }
}

export function getBlockchainAddressLink(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
    cryptoAddress: string,
): string | undefined {
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://etherscan.io/address/${cryptoAddress}`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://rinkeby.etherscan.io/address/${cryptoAddress}`;
        }
        case SupportedImportBlockchains.ropsten: {
            return `https://ropsten.etherscan.io/address/${cryptoAddress}`;
        }
        case SupportedImportBlockchains.goerli: {
            return `https://goerli.etherscan.io/address/${cryptoAddress}`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://polygonscan.com/address/${cryptoAddress}`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://mumbai.polygonscan.com/address/${cryptoAddress}`;
        }
        case SupportedImportBlockchains.solana: {
            return `https://explorer.solana.com/address/${cryptoAddress}`;
        }
    }
}

export function getOpenSeaApiEndpoint(
    blockchain:
        | SupportedImportBlockchains
        | SupportedMintingBlockchainsEnum
        | SwaggerGeneratedImportBlockchains,
): string | undefined {
    switch (blockchain) {
        case SupportedImportBlockchains.ethereum: {
            return `https://api.opensea.io/api/v1/assets`;
        }
        case SupportedImportBlockchains.rinkeby: {
            return `https://testnets-api.opensea.io/api/v1/assets`;
        }
        // appears to be unsupported atm
        // case SupportedImportBlockchains.ropsten: {
        //     return `https://testnets-api.opensea.io/api/v1/assets/ropsten`;
        // }
        case SupportedImportBlockchains.goerli: {
            return `https://testnets-api.opensea.io/api/v1/assets`;
        }
        case SupportedImportBlockchains.polygon: {
            return `https://api.opensea.io/api/v1/assets`;
        }
        case SupportedImportBlockchains.mumbai: {
            return `https://testnets-api.opensea.io/api/v1/assets`;
        }
    }
}

export function getTokenVideoSrc(
    claimedToken: ITokenWithMetadataAndCollection,
): string | undefined {
    let src = "";
    if (claimedToken.tokenMetadata?.cloudVideo) {
        src = claimedToken.tokenMetadata.cloudVideo;
    } else if (claimedToken.tokenMetadata?.ipfsVideoCid) {
        src = `${IPFS_GATEWAY_PREFIX}${claimedToken.tokenMetadata.ipfsVideoCid}`;
    } else if (claimedToken.tokenMetadata?.animationUrl) {
        if (claimedToken.tokenMetadata.animationUrl.startsWith("ipfs://")) {
            src = `${IPFS_GATEWAY_PREFIX}${claimedToken.tokenMetadata.animationUrl.replace(
                "ipfs://",
                "",
            )}`;
        } else {
            src = claimedToken.tokenMetadata.animationUrl;
        }
    }
    return src;
}
